import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { toast, Toaster } from 'react-hot-toast';
import Pagination from '../components/Pagination';

const generateProjectFYYears = (startYear, numYears) => {
  const years = [];
  const sY = parseInt(startYear) || 2026;
  const nY = Math.min(Math.max(parseInt(numYears) || 10, 1), 30);
  for (let i = 0; i < nY; i++) {
    const yr = sY + i;
    years.push(`${yr}-${String(yr + 1).slice(-2)}`);
  }
  return years;
};

const generateGenericFYYears = () => {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();
  const baseYear = currentMonth >= 3 ? currentYear : currentYear - 1;
  const years = [];
  for (let i = baseYear - 2; i <= baseYear + 15; i++) {
    years.push(`${i}-${String(i + 1).slice(-2)}`);
  }
  return years.reverse();
};

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt)) return d;
  return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const emptyForm = (fyYears) => ({
  report_id: '',
  financial_year: fyYears?.[0] || '2028-29',
  percentage_label: 'Growth Rate',
  percentage_value: '',
  notes: '',
});

const ProjectPercentageMaster = ({ isEmbedded, selectedReportId }) => {
  const API = process.env.REACT_APP_API_URL;

  const [projectsList, setProjectsList] = useState([]);
  const [summaryData, setSummaryData] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [autoGenerating, setAutoGenerating] = useState(false);
  const [projectConfig, setProjectConfig] = useState(null);
  const [projectFYYears, setProjectFYYears] = useState(generateGenericFYYears());
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedFY, setSelectedFY] = useState('');
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;
  const [showModal, setShowModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm(null));
  const [confirmDelete, setConfirmDelete] = useState({ open: false, id: null, label: '' });
  const [inlineEdit, setInlineEdit] = useState({});
  // Local Start FY Year and No. of Years — user edits these directly in project-percentage page
  const [localStartYear, setLocalStartYear] = useState(2028);
  const [localNumYears, setLocalNumYears] = useState(10);
  // ── Define callbacks FIRST before any useEffect that calls them ──────────
  const fetchSummary = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/project-percentage/summary`, { withCredentials: true });
      setSummaryData(res.data);
    } catch { }
  }, [API]);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedProject) params.report_id = selectedProject;
      const res = await axios.get(`${API}/api/project-percentage`, { params, withCredentials: true });
      setRecords(res.data);
    } catch { toast.error('Failed to load records'); }
    finally { setLoading(false); }
  }, [API, selectedProject]);

  // ── Effects ───────────────────────────────────────────────────────────────
  useEffect(() => {
    axios.get(`${API}/api/hospital-reports`, { withCredentials: true })
      .then(res => setProjectsList(res.data))
      .catch(() => toast.error('Failed to load projects'));
    fetchSummary();
  }, [API, fetchSummary]);

  useEffect(() => {
    if (isEmbedded && selectedReportId) setSelectedProject(selectedReportId);
  }, [isEmbedded, selectedReportId]);

  useEffect(() => {
    if (!selectedProject) {
      setProjectConfig(null);
      setProjectFYYears(generateGenericFYYears());
      return;
    }
    axios.get(`${API}/api/financial-projection-config/${selectedProject}`, { withCredentials: true })
      .then(res => {
        const cfg = res.data;
        if (cfg && cfg.startYear && cfg.numYears) {
          setProjectConfig(cfg);
          const sY = parseInt(cfg.startYear);
          const nY = parseInt(cfg.numYears);
          setLocalStartYear(sY);
          setLocalNumYears(nY);
          setProjectFYYears(generateProjectFYYears(sY, nY));
        } else {
          setProjectConfig(null);
          // Keep localStartYear/localNumYears at their current default values
          setProjectFYYears(generateProjectFYYears(localStartYear, localNumYears));
        }
      })
      .catch(() => {
        setProjectConfig(null);
        setProjectFYYears(generateProjectFYYears(localStartYear, localNumYears));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API, selectedProject]);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);


  const getProjectSummary = (reportId) => summaryData.find(s => String(s.report_id) === String(reportId));
  const selectedProjectSummary = selectedProject ? getProjectSummary(selectedProject) : null;

  const handleAutoGenerate = async () => {
    if (!selectedProject) return toast.error('Please select a project first');
    const sY = parseInt(localStartYear) || 2028;
    const nY = Math.min(Math.max(parseInt(localNumYears) || 10, 1), 30);
    const fyYears = generateProjectFYYears(sY, nY);
    if (!fyYears.length) return toast.error('No FY years to generate');

    setAutoGenerating(true);
    try {
      // Step 1: Save this config to financial_projection_config so FY Projection knows the range
      await axios.post(`${API}/api/financial-projection-config/${selectedProject}`,
        { ...projectConfig, startYear: sY, numYears: nY },
        { withCredentials: true }
      ).catch(() => { }); // Best-effort — don't block if config save fails

      // Update local FY years list immediately
      setProjectFYYears(fyYears);

      // Step 2: Bulk create the records
      const recordsToCreate = fyYears.map(fy => ({ financial_year: fy, percentage_label: 'Growth Rate', percentage_value: 0, notes: null }));
      const res = await axios.post(`${API}/api/project-percentage/bulk`, { report_id: Number(selectedProject), records: recordsToCreate }, { withCredentials: true });
      const { created, skipped } = res.data;
      if (created > 0) toast.success(`✅ ${created} records created!${skipped > 0 ? ` (${skipped} already existed)` : ''}`);
      else toast(`ℹ️ All ${skipped} records already exist`, { icon: 'ℹ️' });
      fetchRecords(); fetchSummary();
    } catch (err) { toast.error(err?.response?.data?.error || 'Auto-generate failed'); }
    finally { setAutoGenerating(false); }
  };

  const fyTabs = [...new Set(records.filter(r => !selectedProject || String(r.report_id) === String(selectedProject)).map(r => r.financial_year))].sort();

  const openAddModal = () => {
    setIsEdit(false); setEditId(null);
    setForm({ ...emptyForm(projectFYYears), report_id: selectedProject || '', financial_year: selectedFY || projectFYYears[0] || '2028-29' });
    setShowModal(true);
  };

  const openEditModal = (rec) => {
    setIsEdit(true); setEditId(rec.id);
    setForm({ report_id: String(rec.report_id), financial_year: rec.financial_year, percentage_label: rec.percentage_label, percentage_value: String(rec.percentage_value), notes: rec.notes || '' });
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setForm(emptyForm(projectFYYears)); setIsEdit(false); setEditId(null); };

  const handleSave = async () => {
    if (!form.report_id) return toast.error('Please select a project');
    if (!form.financial_year) return toast.error('Please select a financial year');
    if (!form.percentage_label.trim()) return toast.error('Percentage label is required');
    if (form.percentage_value === '' || isNaN(form.percentage_value)) return toast.error('Enter a valid percentage value');
    if (!isEdit) {
      const existing = records.find(r => Number(r.report_id) === Number(form.report_id) && r.financial_year === form.financial_year);
      if (existing) return toast.error(`A record already exists for ${form.financial_year}. Edit it instead.`);
    } else {
      const existing = records.find(r => Number(r.report_id) === Number(form.report_id) && r.financial_year === form.financial_year && r.id !== editId);
      if (existing) return toast.error(`A record already exists for ${form.financial_year}.`);
    }
    setSaving(true);
    try {
      const payload = { report_id: Number(form.report_id), financial_year: form.financial_year, percentage_label: form.percentage_label.trim(), percentage_value: parseFloat(form.percentage_value), notes: form.notes.trim() || null };
      if (isEdit) { await axios.put(`${API}/api/project-percentage/${editId}`, payload, { withCredentials: true }); toast.success('Record updated!'); }
      else { await axios.post(`${API}/api/project-percentage`, payload, { withCredentials: true }); toast.success('Record added!'); }
      closeModal(); fetchRecords(); fetchSummary();
    } catch (err) { toast.error(err?.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleInlineChange = (id, val) => setInlineEdit(prev => ({ ...prev, [id]: val }));

  const handleInlineSave = async (rec) => {
    const newVal = inlineEdit[rec.id];
    if (newVal === undefined || newVal === '') return;
    if (isNaN(newVal)) return toast.error('Enter a valid number');
    try {
      await axios.put(`${API}/api/project-percentage/${rec.id}`, { report_id: rec.report_id, financial_year: rec.financial_year, percentage_label: rec.percentage_label, percentage_value: parseFloat(newVal), notes: rec.notes || null }, { withCredentials: true });
      toast.success(`FY ${rec.financial_year}: ${parseFloat(newVal).toFixed(2)}% saved!`);
      setInlineEdit(prev => { const n = { ...prev }; delete n[rec.id]; return n; });
      fetchRecords();
    } catch (err) { toast.error(err?.response?.data?.error || 'Save failed'); }
  };

  const handleApplyBelow = async (startRec, startIdx) => {
    const val = inlineEdit[startRec.id] !== undefined ? inlineEdit[startRec.id] : startRec.percentage_value;
    if (val === undefined || val === '') return;
    if (isNaN(val)) return toast.error('Enter a valid number');

    const recordsToUpdate = displayedRecords.slice(startIdx);
    try {
      setLoading(true);
      await Promise.all(recordsToUpdate.map(rec =>
        axios.put(`${API}/api/project-percentage/${rec.id}`, {
          report_id: rec.report_id,
          financial_year: rec.financial_year,
          percentage_label: rec.percentage_label,
          percentage_value: parseFloat(val),
          notes: rec.notes || null
        }, { withCredentials: true })
      ));
      toast.success(`Applied ${parseFloat(val).toFixed(2)}% to ${recordsToUpdate.length} records!`);
      setInlineEdit({});
      fetchRecords();
    } catch (err) {
      toast.error('Failed to apply to some records');
      fetchRecords();
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/api/project-percentage/${id}`, { withCredentials: true });
      toast.success('Record deleted!');
      setConfirmDelete({ open: false, id: null, label: '' });
      fetchRecords(); fetchSummary();
    } catch { toast.error('Delete failed'); }
  };

  const getPctColor = (val) => {
    const v = parseFloat(val) || 0;
    if (v >= 20) return 'text-emerald-400';
    if (v >= 10) return 'text-teal-400';
    if (v >= 5) return 'text-amber-400';
    if (v > 0) return 'text-orange-400';
    return 'text-rose-400';
  };


  const filteredForProject = records.filter(r => !selectedProject || String(r.report_id) === String(selectedProject));
  const allDisplayedRecords = (selectedFY ? filteredForProject.filter(r => r.financial_year === selectedFY) : filteredForProject).sort((a, b) => String(a.financial_year).localeCompare(String(b.financial_year)));
  const totalPages = Math.ceil(allDisplayedRecords.length / recordsPerPage);
  const displayedRecords = allDisplayedRecords.slice((currentPage - 1) * recordsPerPage, currentPage * recordsPerPage);

  const innerContent = (
    <>
      <Toaster position="top-right" />

      {/* HEADER STATS */}
      {!isEmbedded && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          {[
            { label: 'Total Projects', value: projectsList.length, color: 'violet', icon: 'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
            { label: 'Total Records', value: records.length, color: 'emerald', icon: 'M7 7h.01M7 3h5l7 7-7 7-5-5V3z' },
            { label: 'Financial Years', value: [...new Set(records.map(r => r.financial_year))].length, color: 'cyan', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
          ].map(({ label, value, color, icon }) => (
            <div key={label} className={`bg-gradient-to-br from-${color}-900/40 to-${color}-800/20 border border-${color}-500/30 rounded-2xl p-5 flex items-center gap-4`}>
              <div className={`w-12 h-12 rounded-xl bg-${color}-500/20 flex items-center justify-center`}>
                <svg className={`w-6 h-6 text-${color}-400`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={icon} /></svg>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-widest font-semibold">{label}</p>
                <p className="text-2xl font-black text-white">{value}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* FILTERS BAR */}
      <div className="bg-slate-900/60 border border-slate-700 rounded-2xl p-5 flex flex-wrap gap-4 items-end mb-6 backdrop-blur-sm">
        {!isEmbedded && (
          <div className="flex-1 min-w-[220px]">
            <label className="block text-xs text-gray-400 font-semibold mb-1.5 uppercase tracking-widest">Filter by Project</label>
            <select className="w-full bg-slate-950 border border-slate-700 p-2.5 rounded-xl text-white text-sm outline-none focus:border-violet-500 transition"
              value={selectedProject} onChange={e => { setSelectedProject(e.target.value); setSelectedFY(''); setCurrentPage(1); }}>
              <option value="">— All Projects —</option>
              {projectsList.map(p => <option key={p.id} value={p.id}>{p.report_name}</option>)}
            </select>
          </div>
        )}

        {/* Start FY Year input — visible when project selected */}
        {selectedProject && (
          <div className="min-w-[120px]">
            <label className="block text-xs text-teal-400 font-bold mb-1.5 uppercase tracking-widest">Start FY Year</label>
            <input type="number" min="2020" max="2050"
              value={localStartYear}
              onChange={e => {
                const sY = parseInt(e.target.value) || 2028;
                setLocalStartYear(sY);
                setProjectFYYears(generateProjectFYYears(sY, localNumYears));
              }}
              className="w-full bg-slate-950 border-2 border-teal-500/40 p-2.5 rounded-xl text-teal-300 text-sm font-black font-mono outline-none focus:border-teal-400 transition"
            />
          </div>
        )}

        {/* No. of Years input — visible when project selected */}
        {selectedProject && (
          <div className="min-w-[100px]">
            <label className="block text-xs text-teal-400 font-bold mb-1.5 uppercase tracking-widest">No. of Years</label>
            <input type="number" min="1" max="30"
              value={localNumYears}
              onChange={e => {
                const nY = Math.min(Math.max(parseInt(e.target.value) || 10, 1), 30);
                setLocalNumYears(nY);
                setProjectFYYears(generateProjectFYYears(localStartYear, nY));
              }}
              className="w-full bg-slate-950 border-2 border-teal-500/40 p-2.5 rounded-xl text-teal-300 text-sm font-black font-mono outline-none focus:border-teal-400 transition"
            />
          </div>
        )}

        <div className="flex-1 min-w-[150px]">
          <label className="block text-xs text-gray-400 font-semibold mb-1.5 uppercase tracking-widest">Financial Year</label>
          <select className="w-full bg-slate-950 border border-slate-700 p-2.5 rounded-xl text-white text-sm outline-none focus:border-cyan-500 transition"
            value={selectedFY} onChange={e => { setSelectedFY(e.target.value); setCurrentPage(1); }}>
            <option value="">— All Years —</option>
            {projectFYYears.map(fy => <option key={fy} value={fy}>FY {fy}</option>)}
          </select>
        </div>

        {/* Auto-Generate: available whenever a project is selected (no config required) */}
        {selectedProject && (
          <button onClick={handleAutoGenerate} disabled={autoGenerating}
            className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-400 hover:to-cyan-400 disabled:opacity-50 text-white px-5 py-2.5 rounded-xl font-bold text-sm transition shadow-lg shadow-teal-500/25 flex items-center gap-2 whitespace-nowrap"
            title={`Create ${localNumYears} year records from FY ${localStartYear}-${String(parseInt(localStartYear) + 1).slice(-2)} (skips existing)`}>
            {autoGenerating
              ? <><svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>Generating...</>
              : <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>Auto-Generate {localNumYears} Years</>
            }
          </button>
        )}
        <button onClick={openAddModal}
          className="bg-gradient-to-r from-violet-500 to-indigo-500 hover:from-violet-400 hover:to-indigo-400 text-white px-6 py-2.5 rounded-xl font-bold text-sm transition shadow-lg shadow-violet-500/25 flex items-center gap-2">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
          Add Percentage
        </button>
      </div>

      {/* CONFIG BANNER */}
      {selectedProject && (
        <div className="bg-gradient-to-r from-teal-950/60 to-cyan-950/40 border border-teal-500/30 rounded-2xl p-4 mb-5 flex flex-wrap gap-3 items-center">
          <div className="flex items-center gap-2 text-teal-400">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
            <span className="text-sm font-bold">Active Configuration</span>
          </div>
          <div className="flex gap-2 flex-wrap text-xs">
            <span className="bg-teal-500/15 border border-teal-500/30 text-teal-300 px-3 py-1.5 rounded-lg font-bold">Start FY: {localStartYear}-{String(parseInt(localStartYear) + 1).slice(-2)}</span>
            <span className="bg-cyan-500/15 border border-cyan-500/30 text-cyan-300 px-3 py-1.5 rounded-lg font-bold">{localNumYears} Years</span>
            <span className="bg-violet-500/15 border border-violet-500/30 text-violet-300 px-3 py-1.5 rounded-lg font-bold">
              FY {localStartYear}-{String(parseInt(localStartYear) + 1).slice(-2)} → {parseInt(localStartYear) + parseInt(localNumYears) - 1}-{String(parseInt(localStartYear) + parseInt(localNumYears)).slice(-2)}
            </span>
          </div>
        </div>
      )}
      {selectedProject && !projectConfig && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-4 mb-5 flex items-start gap-3">
          <svg className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
          <div>
            <p className="text-amber-300 font-bold text-sm">No FY Projection Config Found</p>
            <p className="text-amber-400/70 text-xs mt-0.5">Go to FY Projection tab → set Start FY Year + No. of Years → Save Settings. Then return here to Auto-Generate records.</p>
          </div>
        </div>
      )}

      {/* PROJECT SUMMARY */}
      {selectedProjectSummary && (
        <div className="bg-gradient-to-r from-slate-900/80 to-violet-900/20 border border-violet-500/30 rounded-2xl p-5 mb-6 flex flex-wrap gap-6 items-center">
          <div>
            <p className="text-xs text-gray-400 uppercase tracking-widest mb-1">Selected Project</p>
            <h3 className="text-xl font-black text-white">{selectedProjectSummary.report_name}</h3>
            {selectedProjectSummary.report_date && <p className="text-sm text-gray-400 mt-0.5">📅 {fmtDate(selectedProjectSummary.report_date)}</p>}
          </div>
          <div className="flex gap-4 flex-wrap">
            <div className="bg-violet-500/10 border border-violet-500/30 rounded-xl px-5 py-3 text-center">
              <p className="text-xs text-gray-400 mb-1">Total Records</p>
              <p className="text-2xl font-black text-violet-400">{selectedProjectSummary.total_records || 0}</p>
            </div>
            <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl px-5 py-3 text-center">
              <p className="text-xs text-gray-400 mb-1">Financial Years</p>
              <p className="text-sm font-bold text-cyan-400">{selectedProjectSummary.financial_years ? selectedProjectSummary.financial_years.split(',').join(', ') : '—'}</p>
            </div>
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl px-5 py-3 text-center">
              <p className="text-xs text-gray-400 mb-1">Showing</p>
              <p className="text-2xl font-black text-emerald-400">{displayedRecords.length}</p>
            </div>
          </div>
        </div>
      )}

      {/* FY TABS */}
      {fyTabs.length > 0 && (
        <div className="flex gap-2 mb-4 flex-wrap">
          <button onClick={() => { setSelectedFY(''); setCurrentPage(1); }}
            className={`px-4 py-2 rounded-xl font-bold text-sm transition border ${selectedFY === '' ? 'bg-violet-500/20 border-violet-500/50 text-violet-300' : 'bg-slate-900/60 border-slate-700 text-gray-400 hover:text-white'}`}>All Years</button>
          {fyTabs.map(fy => (
            <button key={fy} onClick={() => { setSelectedFY(fy === selectedFY ? '' : fy); setCurrentPage(1); }}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition whitespace-nowrap shadow-sm border ${fy === selectedFY ? 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30 ring-1 ring-cyan-500/50' : 'bg-slate-900/50 text-slate-400 border-slate-700/50 hover:bg-slate-800 hover:text-slate-300'}`}>
              FY {fy}
              <span className={`ml-2 text-xs px-1.5 py-0.5 rounded-full ${selectedFY === fy ? 'bg-cyan-500/30 text-cyan-300' : 'bg-slate-700 text-gray-400'}`}>
                {records.filter(r => r.financial_year === fy && (!selectedProject || String(r.report_id) === String(selectedProject))).length}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* RECORDS TABLE */}
      <div className="bg-slate-900/60 border border-slate-700 rounded-2xl overflow-hidden backdrop-blur-sm">
        <div className="flex items-center justify-between p-5 border-b border-slate-700/50">
          <div>
            <h2 className="font-black text-white text-lg">{selectedFY ? `FY ${selectedFY} Records` : 'All Percentage Records'}</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {allDisplayedRecords.length} record{allDisplayedRecords.length !== 1 ? 's' : ''} found{selectedProject && ` for selected project`}{selectedFY && ` in FY ${selectedFY}`}
              {selectedProject && projectConfig && <span className="ml-2 text-teal-400 font-semibold">— These % values drive your FY Projection calculations</span>}
            </p>
          </div>
        </div>
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <svg className="w-10 h-10 text-violet-400 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
            <p className="text-gray-400 font-medium">Loading records...</p>
          </div>
        ) : displayedRecords.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <div className="w-16 h-16 rounded-2xl bg-slate-800/60 border border-slate-700 flex items-center justify-center">
              <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
            </div>
            <p className="text-gray-500 font-medium">No records found</p>
            {selectedProject && projectConfig
              ? <button onClick={handleAutoGenerate} disabled={autoGenerating}
                className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-400 hover:to-cyan-400 text-white px-6 py-2.5 rounded-xl font-bold text-sm transition shadow-lg flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                Auto-Generate {projectConfig.numYears} Year Records
              </button>
              : <p className="text-gray-600 text-sm">Click "Add Percentage" to create a new record</p>
            }
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-800/60 border-b border-slate-700">
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-400">#</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-400">Project</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-400">Financial Year</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-400">Label</th>
                  <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-teal-400">Growth % — Edit Inline</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-400">Notes</th>
                  <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/50">
                {displayedRecords.map((rec, idx) => {
                  const currentVal = inlineEdit[rec.id] !== undefined ? inlineEdit[rec.id] : String(rec.percentage_value);
                  const isDirty = inlineEdit[rec.id] !== undefined && String(inlineEdit[rec.id]) !== String(rec.percentage_value);
                  return (
                    <tr key={rec.id} className={`hover:bg-slate-800/30 transition-colors group ${isDirty ? 'bg-teal-900/10' : ''}`}>
                      <td className="px-4 py-3 text-xs text-gray-500 font-mono">{(currentPage - 1) * recordsPerPage + idx + 1}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col">
                          <span className="text-sm font-bold text-white">{rec.report_name}</span>
                          {rec.report_date && <span className="text-xs text-gray-500 mt-0.5">📅 {fmtDate(rec.report_date)}</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-cyan-500/15 border border-cyan-500/30 text-cyan-400">FY {rec.financial_year}</span>
                      </td>
                      <td className="px-4 py-3"><span className="text-sm text-gray-200 font-medium">{rec.percentage_label}</span></td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-16 bg-slate-800 rounded-full h-1.5 hidden sm:block">
                            <div className="h-1.5 rounded-full bg-gradient-to-r from-violet-500 to-cyan-400 transition-all" style={{ width: `${Math.min(100, parseFloat(currentVal) || 0)}%` }} />
                          </div>
                          <div className="flex items-center gap-1">
                            <input type="number" step="0.01" min="0" max="999.99" value={currentVal}
                              onChange={e => handleInlineChange(rec.id, e.target.value)}
                              onKeyDown={e => { if (e.key === 'Enter') handleInlineSave(rec); if (e.key === 'Escape') setInlineEdit(prev => { const n = { ...prev }; delete n[rec.id]; return n; }); }}
                              className={`w-20 bg-slate-900 border rounded-lg px-2 py-1 text-right text-sm font-black font-mono outline-none transition ${isDirty ? 'border-teal-500/60 text-teal-300' : `border-slate-700 focus:border-cyan-500 ${getPctColor(currentVal)}`}`}
                            />
                            <span className="text-gray-500 text-xs">%</span>
                            {isDirty && (
                              <button onClick={() => handleInlineSave(rec)} title="Save (Enter)"
                                className="p-1.5 rounded-lg bg-teal-500/20 hover:bg-teal-500/40 text-teal-400 transition border border-teal-500/30">
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" /></svg>
                              </button>
                            )}
                            <button onClick={() => handleApplyBelow(rec, idx)} title="Apply to all records below"
                              className="p-1.5 rounded-lg bg-indigo-500/10 hover:bg-indigo-500/30 text-indigo-400 transition border border-indigo-500/30 opacity-70 hover:opacity-100 hidden sm:block">
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 14l-7 7m0 0l-7-7m7 7V3" /></svg>
                            </button>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 max-w-[160px]"><span className="text-xs text-gray-400 line-clamp-2">{rec.notes || '—'}</span></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1.5 opacity-70 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => openEditModal(rec)} className="p-2 rounded-lg bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 transition border border-cyan-500/20" title="Edit">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                          </button>
                          <button onClick={() => setConfirmDelete({ open: true, id: rec.id, label: rec.percentage_label })} className="p-2 rounded-lg bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 transition border border-rose-500/20" title="Delete">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {totalPages > 1 && (
               <div className="bg-slate-900/60 p-2 border-t border-slate-700/50">
                 <Pagination currentPage={currentPage} totalPages={totalPages} setCurrentPage={setCurrentPage} totalItems={allDisplayedRecords.length} />
               </div>
            )}
          </div>
        )}
      </div>

      {/* ALL PROJECTS SUMMARY */}
      {!selectedProject && summaryData.length > 0 && (
        <div className="mt-6 bg-slate-900/60 border border-slate-700 rounded-2xl overflow-hidden backdrop-blur-sm">
          <div className="p-5 border-b border-slate-700/50">
            <h2 className="font-black text-white text-lg">All Projects Summary</h2>
            <p className="text-xs text-gray-400 mt-0.5">Record count per project across all financial years</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-800/60 border-b border-slate-700">
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-400">#</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-400">Project Name</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-400">Report Date</th>
                  <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider text-gray-400">Total Records</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-400">Financial Years</th>
                  <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider text-gray-400">Quick Filter</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/50">
                {summaryData.map((proj, idx) => (
                  <tr key={proj.report_id} className="hover:bg-slate-800/30 transition-colors">
                    <td className="px-4 py-3 text-xs text-gray-500 font-mono">{idx + 1}</td>
                    <td className="px-4 py-3"><span className="text-sm font-bold text-white">{proj.report_name}</span></td>
                    <td className="px-4 py-3"><span className="text-sm text-gray-400">{fmtDate(proj.report_date)}</span></td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-bold ${proj.total_records > 0 ? 'bg-emerald-500/15 border border-emerald-500/30 text-emerald-400' : 'bg-slate-700/50 border border-slate-600 text-gray-500'}`}>{proj.total_records}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {proj.financial_years ? proj.financial_years.split(',').map(fy => <span key={fy} className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-cyan-500/10 border border-cyan-500/20 text-cyan-400">{fy.trim()}</span>) : <span className="text-xs text-gray-600">No records yet</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button onClick={() => { setSelectedProject(String(proj.report_id)); setSelectedFY(''); setCurrentPage(1); }} className="px-3 py-1.5 rounded-lg bg-violet-500/10 hover:bg-violet-500/20 text-violet-400 text-xs font-bold border border-violet-500/20 transition">View Records</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ADD/EDIT MODAL */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#0B1120] border border-white/10 rounded-3xl shadow-2xl w-full max-w-lg">
            <div className="flex items-center justify-between px-6 py-5 border-b border-white/[0.06]">
              <div>
                <h3 className="text-lg font-black text-white">{isEdit ? '✏️ Edit Percentage Record' : '➕ Add Percentage Record'}</h3>
                <p className="text-xs text-gray-400 mt-0.5">{isEdit ? 'Update the percentage value' : 'Add a new FY-wise percentage for a project'}</p>
              </div>
              <button onClick={closeModal} className="p-2 rounded-xl hover:bg-white/5 text-gray-400 hover:text-white transition">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-xs text-gray-400 font-semibold mb-1.5 uppercase tracking-widest">Project <span className="text-rose-400">*</span></label>
                <select className="w-full bg-slate-950 border border-slate-700 p-2.5 rounded-xl text-white text-sm outline-none focus:border-violet-500 transition"
                  value={form.report_id} onChange={e => setForm(f => ({ ...f, report_id: e.target.value }))}>
                  <option value="">— Select Project —</option>
                  {projectsList.map(p => <option key={p.id} value={p.id}>{p.report_name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-400 font-semibold mb-1.5 uppercase tracking-widest">
                  Financial Year <span className="text-rose-400">*</span>
                  {projectConfig && <span className="ml-2 text-teal-400 normal-case font-normal text-xs">({projectConfig.numYears} yrs from {projectConfig.startYear})</span>}
                </label>
                <select className="w-full bg-slate-950 border border-slate-700 p-2.5 rounded-xl text-white text-sm outline-none focus:border-cyan-500 transition"
                  value={form.financial_year} onChange={e => setForm(f => ({ ...f, financial_year: e.target.value }))}>
                  {projectFYYears.map(fy => <option key={fy} value={fy}>FY {fy}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-400 font-semibold mb-1.5 uppercase tracking-widest">Percentage Label <span className="text-rose-400">*</span></label>
                <input type="text" placeholder="e.g., Growth Rate, Profit Margin..."
                  className="w-full bg-slate-950 border border-slate-700 p-2.5 rounded-xl text-white text-sm outline-none focus:border-emerald-500 transition placeholder-gray-600"
                  value={form.percentage_label} onChange={e => setForm(f => ({ ...f, percentage_label: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs text-gray-400 font-semibold mb-1.5 uppercase tracking-widest">Percentage Value (%) <span className="text-rose-400">*</span></label>
                <div className="relative">
                  <input type="number" step="0.01" min="0" max="999.99" placeholder="0.00"
                    className="w-full bg-slate-950 border border-slate-700 p-2.5 pr-10 rounded-xl text-white text-sm outline-none focus:border-emerald-500 transition placeholder-gray-600 font-mono"
                    value={form.percentage_value} onChange={e => setForm(f => ({ ...f, percentage_value: e.target.value }))} />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-sm">%</span>
                </div>
                {form.percentage_value !== '' && !isNaN(form.percentage_value) && (
                  <div className="mt-2">
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>Preview</span><span className={getPctColor(form.percentage_value)}>{parseFloat(form.percentage_value).toFixed(2)}%</span>
                    </div>
                    <div className="w-full bg-slate-800 rounded-full h-1.5">
                      <div className="h-1.5 rounded-full bg-gradient-to-r from-violet-500 to-cyan-400 transition-all duration-300" style={{ width: `${Math.min(100, parseFloat(form.percentage_value) || 0)}%` }} />
                    </div>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-xs text-gray-400 font-semibold mb-1.5 uppercase tracking-widest">Notes <span className="text-gray-600">(optional)</span></label>
                <textarea rows={2} placeholder="Any additional notes or remarks..."
                  className="w-full bg-slate-950 border border-slate-700 p-2.5 rounded-xl text-white text-sm outline-none focus:border-slate-500 transition placeholder-gray-600 resize-none"
                  value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
              </div>
            </div>
            <div className="flex gap-3 px-6 py-4 border-t border-white/[0.06]">
              <button onClick={closeModal} className="flex-1 bg-slate-800 hover:bg-slate-700 text-gray-300 px-4 py-2.5 rounded-xl font-bold text-sm transition border border-slate-700">Cancel</button>
              <button onClick={handleSave} disabled={saving}
                className="flex-1 bg-gradient-to-r from-violet-500 to-indigo-500 hover:from-violet-400 hover:to-indigo-400 text-white px-4 py-2.5 rounded-xl font-bold text-sm transition disabled:opacity-40 flex items-center justify-center gap-2">
                {saving ? (<><svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>Saving...</>) : (<>{isEdit ? '✓ Update Record' : '+ Save Record'}</>)}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE CONFIRM */}
      {confirmDelete.open && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#0B1120] border border-rose-500/30 rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <div className="w-12 h-12 rounded-xl bg-rose-500/15 border border-rose-500/30 flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-rose-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
            </div>
            <h3 className="text-center text-lg font-black text-white mb-2">Delete Record?</h3>
            <p className="text-center text-sm text-gray-400 mb-6">Are you sure you want to delete <span className="text-white font-bold">"{confirmDelete.label}"</span>? This cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDelete({ open: false, id: null, label: '' })} className="flex-1 bg-slate-800 hover:bg-slate-700 text-gray-300 px-4 py-2.5 rounded-xl font-bold text-sm transition border border-slate-700">Cancel</button>
              <button onClick={() => handleDelete(confirmDelete.id)} className="flex-1 bg-rose-600 hover:bg-rose-500 text-white px-4 py-2.5 rounded-xl font-bold text-sm transition">Yes, Delete</button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  if (isEmbedded) return <div className="embedded-project-percentage">{innerContent}</div>;

  return (
    <Layout title="Project Percentage Master" subtitle="Manage financial year-wise percentage values per project">
      {innerContent}
    </Layout>
  );
};

export default ProjectPercentageMaster;
